import type { Ref } from "vue";

import {
  usePreferredReducedMotion,
  useRafFn,
  useTimeoutFn,
} from "@vueuse/core";
import { watch } from "vue";

type Options = {
  animationDelay: number;
  scrollSpeed: number;
};

/**
 * Drives a seamless horizontal auto-scroll on a duplicated-content carousel.
 *
 * Pauses on user interaction (hover / focus / pointer / wheel / manual scroll)
 * and on `prefers-reduced-motion: reduce`. Resumes after `animationDelay` ms.
 */
function useAutoScrollCarousel(
  scrollContainerRef: Ref<HTMLElement | null>,
  { animationDelay, scrollSpeed }: Options,
) {
  const reducedMotion = usePreferredReducedMotion();

  let isProgrammaticScroll = false;
  let scrollRemainder = 0;

  const { pause: pauseAutoScroll, resume: resumeAutoScroll } = useRafFn(
    () => {
      const scrollContainer = scrollContainerRef.value;
      if (scrollContainer === null) return;
      scrollRemainder = scrollRemainder + scrollSpeed;
      const delta = Math.floor(scrollRemainder);
      if (delta < 1) return;
      scrollRemainder = scrollRemainder - delta;
      isProgrammaticScroll = true;
      scrollContainer.scrollLeft = scrollContainer.scrollLeft + delta;
      wrapScrollPosition(scrollContainer);
    },
    { immediate: false },
  );

  const { start: scheduleResume, stop: cancelResume } = useTimeoutFn(
    resumeAutoScroll,
    animationDelay,
    { immediate: false },
  );

  watch(
    [scrollContainerRef, reducedMotion],
    ([scrollContainer, motion]) => {
      if (scrollContainer === null || motion === "reduce") {
        pauseAutoScroll();
      } else {
        resumeAutoScroll();
      }
    },
    { immediate: true },
  );

  function wrapScrollPosition(scrollContainer: HTMLElement) {
    const loopDistance = getLoopDistance(scrollContainer);
    if (loopDistance <= 0) return;

    if (scrollContainer.scrollLeft >= loopDistance) {
      isProgrammaticScroll = true;
      scrollContainer.scrollLeft = scrollContainer.scrollLeft - loopDistance;
    } else if (scrollContainer.scrollLeft < 0) {
      isProgrammaticScroll = true;
      scrollContainer.scrollLeft = scrollContainer.scrollLeft + loopDistance;
    }
  }

  /**
   * Ignoring the gap leaves a half-gap visual jump on every wrap.
   */
  function getLoopDistance(scrollContainer: HTMLElement): number {
    const list = scrollContainer.firstElementChild;
    const gap = list
      ? Number.parseFloat(window.getComputedStyle(list).columnGap) || 0
      : 0;
    return (scrollContainer.scrollWidth + gap) / 2;
  }

  function pauseAuto() {
    pauseAutoScroll();
    cancelResume();
  }

  function onUserInteraction() {
    pauseAuto();
    scheduleResume();
  }

  function onScroll() {
    if (isProgrammaticScroll) {
      isProgrammaticScroll = false;
      return;
    }
    onUserInteraction();
    const scrollContainer = scrollContainerRef.value;
    if (scrollContainer) {
      wrapScrollPosition(scrollContainer);
    }
  }

  return {
    onScroll,
    onUserInteraction,
    pauseAuto,
    scheduleResume,
  };
}

export { useAutoScrollCarousel };
